import { ArrayUtils, engineComponent } from "neo-common";
import { ComponentTypes } from "../Components/ComponentTypes";
import { Machine } from "../GameComponents/Machine";
import { CreateFrameTimer, FrameTimerEventEmitter, FrameTimerEvents } from "../Utils/FrameTimer";
import { PlayList } from "../Components/Sounds/PlayList";
import { AppContext } from "../../Common/AppContext";
import { ModuleManager } from "../Managers/ModuleManager";
import { DataManager } from "../Managers/DataManager";
import { GameData } from "../Channels/Common/Datas/GameData";
import { ReelTeasingActions, IReelTeasingAttributes, TeasingComponentAbstract } from "./TeasingComponentAbstract";
import { Symbol } from "../GameComponents/Symbol";
import { ReelExtended } from "./ReelExtended";
import { TeasingMystery } from "./TeasingMystery";
import { CustomUtils } from "../Utils/CustomUtils";
import { Settings } from "./Settings";
import { FreeRoundBonus } from "../Channels/Common/Bonuses/FreeRoundBonus";

/**
 * Machine handle reels and symbols for reels rotation.
 * Machine define behaviour for complete flow of reel rotation.
 * @class Machine
 * @extends Machine
 */
@engineComponent(ComponentTypes.DisplayObject)
export class MachineExtended extends Machine {
    protected _reels: Array<ReelExtended>;
    protected _spinnedReelsCounter: number = 0; // helper to count spinned reels
    protected _stoppingReelsCounter: number = 0; // helper to count stopping reels
    private spinBitmask: number;

    //emit machineStopped with delay
    private _machineStopTimer: FrameTimerEventEmitter;

    constructor() {
        super();

        this._machineStopTimer = CreateFrameTimer("MachineStoppedDelay");
        this._machineStopTimer.on(FrameTimerEvents.Complete, this.onMachineStoppedDelayed.bind(this));

        this.spinBitmask = 0;
    }

    /**
     * Start spinning reels.
     * @method spinStart
     * @param longRotation boolean
     * @param useTransform Use transformed symbols in showSymbols()
     * @param currentSymbols
     * @param spinBitmask
     * @return void
     */
    public spinStart(
        longRotation?: boolean,
        useTransform: boolean = false,
        currentSymbols?: Array<Array<Symbol>>,
        spinBitmask: number = 0,
    ): void {
        this.spinBitmask = spinBitmask;

        if (this.spinBitmask !== 0) {
            if (DEBUG) {
                this.log("spinStart");
            }
            this._stoppedFromServer = false;
            this._stoppedFromMachine = false;
            this._stoppedFromLegal = false;

            this._startSpinTime = Date.now();

            this.visible = true;
            this._reelsContainer.visible = true;

            this.actualReelStopped = -1;
            this._stoppedReelIndexes = 0;
            this._spinnedReelsCounter = 0;
            this._stoppingReelsCounter = 0;

            if (this._reels != null) {
                for (let i: number = 0; i < this._reelCount; i++) {
                    if (this._reels[i] != null) {
                        this._reels[i].previousReel = null;
                        if ((spinBitmask & (1 << i)) !== 0) {
                            if (this._stoppedSymbolsArray.length > 0) {
                                // re-set previous reel
                                if (i > 0) {
                                    for (let j = i - 1; j >= 0; j--) {
                                        if ((spinBitmask & (1 << j)) !== 0) {
                                            this._reels[i].previousReel = this._reels[j];
                                            break;
                                        }
                                    }
                                }

                                this._reels[i].visible = true;
                                this._reels[i].rollDirection = 1;
                                this._reels[i].setData(this._stoppedSymbolsArray[i]);
                                this._reels[i].addCurrentSymbolsToReel(currentSymbols[i]);
                                this._reels[i].showSymbols(useTransform);
                                this._reels[i].spinStart();
                                this._spinnedReelsCounter++;
                            } else {
                                if (DEBUG) {
                                    this.log("ERROR no stopped symbols");
                                }
                            }
                        }
                    } else {
                        if (DEBUG) {
                            this.log("ERROR no reel with index: " + i);
                        }
                    }
                }

                if (longRotation != null && longRotation) {
                    this._stopTimer.start(this._minSpinRotationLong);
                } else {
                    this._stopTimer.start(this._minSpinRotation);
                }
            } else {
                if (DEBUG) {
                    this.log("ERROR no reels");
                }
            }

            PlayList.play("SND_BaseRotationStart");
            PlayList.loop("SND_BaseRotation", 0, true);
        }
    }

    /**
     * Start spinning reels.
     * @method spinStart
     * @param longRotation boolean
     * @param useTransform Use transformed symbols in showSymbols()
     * @param currentSymbols
     * @param spinBitmask
     * @param stickedWildsCount
     * @return void
     */
    public respinStart(
        longRotation?: boolean,
        useTransform: boolean = false,
        currentSymbols?: Array<Array<Symbol>>,
        spinBitmask: number = 0,
    ): void {
        this.spinBitmask = spinBitmask;

        if (this.spinBitmask !== 0) {
            if (DEBUG) {
                this.log("spinStart");
            }
            this._stoppedFromServer = false;
            this._stoppedFromMachine = false;
            this._stoppedFromLegal = false;

            this.visible = true;
            this._reelsContainer.visible = true;

            this.actualReelStopped = -1;
            this._stoppedReelIndexes = 0;
            this._spinnedReelsCounter = 0;
            this._stoppingReelsCounter = 0;

            if (this._reels != null) {
                for (let i: number = 0; i < this._reelCount; i++) {
                    if (this._reels[i] != null) {
                        this._reels[i].previousReel = null;
                        if ((spinBitmask & (1 << i)) !== 0) {
                            if (this._stoppedSymbolsArray.length > 0) {
                                // re-set previous reel
                                if (i > 0) {
                                    for (let j = i - 1; j >= 0; j--) {
                                        if ((spinBitmask & (1 << j)) !== 0) {
                                            this._reels[i].previousReel = this._reels[j];
                                            break;
                                        }
                                    }
                                }

                                this._reels[i].visible = true;
                                this._reels[i].rollDirection = 1;
                                this._reels[i].setData(this._stoppedSymbolsArray[i]);
                                this._reels[i].addCurrentSymbolsToReel(currentSymbols[i]);
                                this._reels[i].showSymbols(useTransform);
                                this._reels[i].respinStart();
                                this._spinnedReelsCounter++;
                            } else {
                                if (DEBUG) {
                                    this.log("ERROR no stopped symbols");
                                }
                            }
                        }
                    } else {
                        if (DEBUG) {
                            this.log("ERROR no reel with index: " + i);
                        }
                    }
                }

                if (longRotation != null && longRotation) {
                    this._stopTimer.start(this._minSpinRotationLong * 2.5);
                } else {
                    this._stopTimer.start(this._minSpinRotation * 2.5);
                }
            } else {
                if (DEBUG) {
                    this.log("ERROR no reels");
                }
            }

            PlayList.play("SND_BaseRotationStart");
            PlayList.loop("SND_BaseRotation", 0, true);
        }
    }

    /**
     * Stop reels rotation.
     * @method spinStop
     * @param event any - from timer
     * @param force boolean - default false used when spin_stop was dispatch
     * @param fromServer boolean - default false used when spin_stop was dispatch from server
     * @param legal
     * @return void
     */
    public spinStop(event: any, force: boolean = false, fromServer: boolean = false, legal: boolean = false): void {
        if (this.spinBitmask !== 0) {
            this.forceStop = force;

            if (fromServer) {
                this._stoppedFromServer = true;
                if (this._legalStopTimer != null) {
                    clearTimeout(this._legalStopTimer);
                }
                if (this.verifyMinGameRoundDurationConditions()) {
                    this._legalStopTimer = setTimeout(
                        () => this.spinStop(null, false, false, true),
                        this.getLegalRemainingSpinDuration(),
                    );
                } else {
                    this._stoppedFromLegal = true;
                }
            } else if (legal) {
                if (this._legalStopTimer != null) {
                    clearTimeout(this._legalStopTimer);
                }
                this._stoppedFromLegal = true;
            } else {
                this._stopTimer.stop();
                this._stoppedFromMachine = true;
            }

            if (force) {
                //PlayList.play('SND_BaseRotationEndQuick');

                if (this._legalStopTimer != null) {
                    clearTimeout(this._legalStopTimer);
                }
                this._stoppedFromLegal = true;
                this._stopTimer.stop();
                this._stoppedFromMachine = true;
            }

            if (DEBUG) {
                this.log("spinStop: " + force);
            }

            let rta: Array<IReelTeasingAttributes>;
            if (this.elementInstance["teasing"] != null) {
                const self = this;
                this.elementInstance["teasing"].forEach(function (teasingComponent: TeasingComponentAbstract) {
                    let reelTeasingAttributes = teasingComponent.checkOnInit({
                        force: force,
                        stoppedSymbols: self._stoppedSymbolsArray,
                        spinBitmask: self.spinBitmask,
                    });
                    if (teasingComponent.teasingEnabled) {
                        rta = reelTeasingAttributes;
                    }
                }, this);
            }

            if (this._stoppedFromServer && this._stoppedFromMachine && this._stoppedFromLegal) {
                if (
                    !CustomUtils.isRespinActive() &&
                    !CustomUtils.isRespinLast() &&
                    !(FreeRoundBonus.inFrb && FreeRoundBonus.winPayMethod === "cumulative")
                ) {
                    DataManager.updateDataElement(GameData.DataTotalWin, "0");
                    DataManager.updateDataElement(GameData.DataTotalWinDenom, "0");
                }

                if (this._reels != null) {
                    for (let i: number = 0; i < this._reelCount; i++) {
                        if (this._reels[i] != null) {
                            if ((this.spinBitmask & (1 << i)) !== 0) {
                                // Stop only spinned reels.
                                if (this._stoppedSymbolsArray.length !== 0) {
                                    this._stoppingReelsCounter++;
                                    this._reels[i].setData(this._stoppedSymbolsArray[i]);
                                    if (typeof rta !== "undefined") {
                                        if (i == this._reelCount - 1) {
                                            // last reel
                                            if (this._stoppedReelIndexes === this._reelCount - 1) {
                                                this._reels[i].spinStop(force, rta[i].stopDelay);
                                            } else {
                                                this._reels[i].spinStop(false, rta[i].stopDelay);
                                            }
                                        } else {
                                            this._reels[i].spinStop(force, rta[i].stopDelay);
                                        }

                                        if (this._stoppingReelsCounter === 1) {
                                            //this.hideNonTeasableSymbols(i);
                                        }
                                    } else {
                                        this._reels[i].spinStop(force);
                                    }
                                } else {
                                    if (DEBUG) {
                                        this.log("ERROR no stopped symbols");
                                    }
                                }
                            }
                        } else {
                            if (DEBUG) {
                                this.log("ERROR no reel with index: " + i);
                            }
                        }
                    }
                } else {
                    if (DEBUG) {
                        this.log("ERROR no reels");
                    }
                }
            }
        } else {
            this.emit(Machine.MachineStopped);
        }
    }

    /**
     * Dispatched onMachineStopped when all reels are stopped.
     * @method onReelStopped
     * @param reelIndex number - reel index
     * @return void
     */
    public onReelStopped(reelIndex: number): void {
        if (DEBUG) {
            this.log("reel stopped: " + reelIndex);
        }

        this.hideNonTeasableSymbols(reelIndex);

        PlayList.play("SND_BaseRotationEnd" + reelIndex);

        this._stoppedReelIndexes++;
        this.actualReelStopped = reelIndex;
        this._reels[reelIndex].visible = false;

        if (this.elementInstance["teasing"] != null) {
            this.elementInstance["teasing"].forEach(function (teasingComponent: TeasingComponentAbstract) {
                teasingComponent.animateTeasingOnReelStopped(reelIndex);
            });
        }

        this.emit(Machine.ReelStopped);

        if (this._stoppedReelIndexes === this._spinnedReelsCounter) {
            let teasing = false;

            if (this.elementInstance["teasing"] != null) {
                this.elementInstance["teasing"].forEach(function (teasingComponent: TeasingComponentAbstract) {
                    if (teasingComponent.teasingEnabled) {
                        teasing = true;
                    }

                    teasingComponent.animationStop();
                });
            }

            this.onMachineStoppedDelayed();
        }
    }

    /**
     * If there is wild on the last reel, dispatched onMachineStopped with delay.
     * @method onReelStoppedDelayed
     * @return void
     */
    private onMachineStoppedDelayed(): void {
        this._machineStopTimer.stop();

        PlayList.stop("SND_BaseRotation", 10);
        if (this._legalStopTimer != null) {
            clearTimeout(this._legalStopTimer);
        }
        this._stopTimer.stop();

        if (DEBUG) {
            const old = AppContext.gameConfiguration.MinGameRoundDuration;
            const current = AppContext.gameConfiguration.GameRoundMinSpan;
            const minDuration = current > 0 ? current : old > 0 ? old : 0;
            const spinDuration = Date.now() - this._startSpinTime;

            if (spinDuration > minDuration || !this.verifyMinGameRoundDurationConditions()) {
                this.log("Spin time duration: " + spinDuration + " ms");
            } else {
                console.log("%cError spin time duration:" + spinDuration + " ms", "color: #ff9900");
            }
        }

        this.emit(Machine.MachineStopped);
        this._reelsContainer.visible = false;
    }

    /**
     *
     * @param reelIndex
     */
    private hideNonTeasableSymbols(reelIndex: number): void {
        if (this.elementInstance["teasing"] != null) {
            const self = this;
            this.elementInstance["teasing"].forEach(function (teasingComponent: TeasingComponentAbstract) {
                if (teasingComponent instanceof TeasingMystery) {
                    if (teasingComponent.teasingEnabled) {
                        for (let i: number = 0; i < this._reelCount; i++) {
                            if (teasingComponent.reelsTeasingAttributes[i].action == ReelTeasingActions.TEASE) {
                                self._reels[i].setTease(true, teasingComponent.getTeasingSymbols());
                            }
                        }
                    }
                }
            }, this);
        }
    }

    /**
     * Add secondary reels data from base.initData.customData.respinReels. Custom for Goblinions.
     * @param reels
     */
    public addRespinReels(reels: Array<Array<number>>): void {
        for (let i: number = 0; i < this._reelCount; i++) {
            this._reels[i].addCustomData({ respinReels: reels[i] });
        }
    }

    public addAdditionalReelSets(reelSets: any): void {
        for (let reelIndex: number = 0; reelIndex < this._reelCount; reelIndex++) {
            let reelSetsForReel: any = [];
            for (let i = 0; i < reelSets.length; i++) {
                reelSetsForReel.push({
                    id: reelSets[i].id,
                    reel: reelSets[i].reels[reelIndex],
                });
            }
            this._reels[reelIndex].addCustomData({ reelSets: reelSetsForReel });
        }
    }
}



/** WEBPACK FOOTER **
 ** ./Engine/Game/CustomComponents/MachineExtended.ts
 **/